import React, { FC, useState, useMemo, useEffect, memo, FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from '@reach/router';
import { AxiosError } from 'axios';
import clsx from 'clsx';

import { Button } from 'components/Button';
import { OverlaySpinner } from 'components/Spinner';
import { Customer, Source, SourceType, WalletResponse } from 'services/checkout-service/models';
import { useCloverPaymentIframeContext } from 'hooks/context/cloverPaymentIframe';
import { fromCentsWithLocale } from 'utils/Currency';
import { doRecaptcha, getRecaptchaSiteKey } from 'utils/Recaptcha';
import { isForbiddenRequestError } from 'utils/Axios';
import { handleSuccessRedirect } from 'utils/Redirection';
import {
  useCheckoutCurrency,
  useCheckoutSessionContext,
  useCheckoutLocale,
} from 'hooks/context/checkoutSession';
import { useMerchantPaymentConfigContext } from 'hooks/context/merchantPaymentConfig';
import { useCalcContext } from 'hooks/context/calc';
import { useGiftCards } from 'hooks/context/giftcards';

import {
  CustomerInfo,
  getAddress,
  getAddressInfo,
  getBusinessName,
  getCustomerEmail,
  getNames,
  getPhoneNumber,
} from '../CustomerInfo';
import { PaymentSourceForm } from '../PaymentSourceForm';
import { PaymentInfoErrors } from '../PaymentInfo';
import { NotifyWalletOfPaymentType, WalletButtons } from '../WalletButtons';

import { MAX_VARIABLE_AMOUNT } from '../Totals/const';
import { useCloverPay } from './useCloverPay';
import { PAYMENT_METHODS_OPTIONS, PaymentMethods } from '../PaymentMethods';
import { CLOVER_PAYMENT_SECTION_IDS } from './const';
import { getApiErrorMessage, isValid, isTouchedAndValid, calculateFinalGiftCardAmount } from './utils';

import styles from './styles.module.scss';

type CloverPaymentSectionProps = {
  children?: never;
  checkoutSessionId?: string;
  hasMaxPayAttemptsReached: boolean;
  total: number;
  isInvoiceMode: boolean;
  isPayWidget: boolean;
  customer?: Customer;
  minAmount?: number;
  payWidgetAmount: number;
  subtotal: number;
};

export type ErrorInputState = {
  hasError: boolean;
  isTouched: boolean;
};


export type EmailInputState = ErrorInputState & {
  invalidEmail?: string;
};

export type NameInputState = {
  firstName: ErrorInputState;
  lastName: ErrorInputState;
};

export type AddressInputState = {
  streetAddress1: ErrorInputState;
  streetAddress2: ErrorInputState;
  city: ErrorInputState;
  state: ErrorInputState;
  zip: ErrorInputState;
};

export type CustomerInfoFields = {
  ECOMM_CHECKOUT_CUSTOMER_INFO_BILLING_ADDRESS_ENABLED: string;
  ECOMM_CHECKOUT_CUSTOMER_INFO_BILLING_ADDRESS_REQUIRED: string;
  ECOMM_CHECKOUT_CUSTOMER_INFO_PHONE_NUMBER_ENABLED: string;
  ECOMM_CHECKOUT_CUSTOMER_INFO_PHONE_NUMBER_REQUIRED: string;
  ECOMM_CHECKOUT_CUSTOMER_INFO_BUSINESS_NAME_ENABLED: string;
  ECOMM_CHECKOUT_CUSTOMER_INFO_BUSINESS_NAME_REQUIRED: string;
};

export type SetPaymentOverlayType = (isInProgress: boolean) => void;

const CloverPaymentSection: FC<CloverPaymentSectionProps> = memo(props => {
  const {
    checkoutSessionId,
    hasMaxPayAttemptsReached,
    total,
    isInvoiceMode,
    isPayWidget,
    customer,
    minAmount,
    payWidgetAmount,
    subtotal,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cloverElementsInstance, cloverInstance } = useCloverPaymentIframeContext();
  const { pendingAmount, isVariableAmount, payableTotalAmount, convenienceFee, tipAmount } = useCalcContext();
  const { giftCards } = useGiftCards();
  const { tokenize, pay } = useCloverPay();
  const initialEmail = getCustomerEmail(customer);
  const initialNames = getNames(customer);
  const initialBusinessName = getBusinessName(customer);
  const initialPhoneNumber = getPhoneNumber(customer);
  const initialAddresses = getAddress(customer);
  const { merchantSettings } = useCheckoutSessionContext();
  const currency = useCheckoutCurrency();
  const locale = useCheckoutLocale();
  const recaptchaSiteKey = getRecaptchaSiteKey();
  const { merchantPaymentConfig } = useMerchantPaymentConfigContext();
  const recaptchaRef: React.RefObject<ReCAPTCHA> = React.createRef();
  // const [recaptchaRef, setRecaptchaRef] = useState<React.RefObject<ReCAPTCHA>>(React.createRef());
  const isRecaptchaEnabled =
    merchantSettings?.ECOMM_CHECKOUT_RECAPTCHA_ENABLED === 'true' ||
    merchantSettings?.ECOMM_CHECKOUT_RECAPTCHA_ENABLED === '1';
  const isCustomerInfoFeatureEnabled =
    merchantSettings?.HCO_CUSTOMER_INFO_FEATURE_ENABLED === 'true' ||
    merchantSettings?.HCO_CUSTOMER_INFO_FEATURE_ENABLED === '1';
  const customerInfoSettings: CustomerInfoFields =
    merchantSettings &&
    merchantSettings.HCO_CUSTOMER_INFO_SETTINGS_ENABLED &&
    JSON.parse(merchantSettings.HCO_CUSTOMER_INFO_SETTINGS_ENABLED);
  const isCheckoutMode = useMemo(() => !isInvoiceMode && !isPayWidget, [isInvoiceMode, isPayWidget]);
  const isMinAmtSatisfied = isVariableAmount
    ? (!minAmount
        ? payableTotalAmount > 0
        : payableTotalAmount >= minAmount
      )
    : true;
  const [paymentMethod, setPaymentMethod] = useState<string>(PAYMENT_METHODS_OPTIONS[0].key);

  // If the initialEmailAddress is set, we can assume that the error is false, and touched is true.
  const [emailInputState, setEmailInputState] = useState<EmailInputState>({
    hasError: false,
    isTouched: !!initialEmail || false,
  });

  const [nameInputState, setNameInputState] = useState<NameInputState>({
    firstName: {
      hasError: false,
      isTouched: !!initialNames.firstName || false,
    },
    lastName: {
      hasError: false,
      isTouched: !!initialNames.lastName || false,
    },
  });

  const [businessNameInputState, setBusinessNameInputState] = useState<ErrorInputState>({
    hasError: false,
    isTouched: !!initialBusinessName || false,
  });

  const [phoneNumberInputState, setPhoneNumberInputState] = useState<ErrorInputState>({
    hasError: false,
    isTouched: !!initialPhoneNumber || false,
  });

  const [addressInputState, setAddressInputState] = useState<AddressInputState>({
    streetAddress1: {
      hasError: false,
      isTouched: !!initialAddresses?.address1 || false,
    },
    streetAddress2: {
      hasError: false,
      isTouched: !!initialAddresses?.address2 || false,
    },
    city: {
      hasError: false,
      isTouched: !!initialAddresses?.city || false,
    },
    state: {
      hasError: false,
      isTouched: !!initialAddresses?.state || false,
    },
    zip: {
      hasError: false,
      isTouched: !!initialAddresses?.zip || false,
    },
  });

  const [customerInfo, setCustomerInfo] = useState<Partial<Customer> | undefined>(customer);
  const [orderNote, setOrderNote] = useState('');
  const [showNoteError, setShowNoteError] = useState(false);
  const [paymentInfoErrors, setPaymentInfoErrors] = useState<PaymentInfoErrors>();
  const [showPaymentErrors, setShowPaymentErrors] = useState(false);
  const [enableSubmitButton, setEnableSubmitButton] = useState(false);
  const [showOverlaySpinner, setShowOverlaySpinner] = useState(false);
  const [showPaymentApiError, setShowPaymentApiError] = useState(false);
  const [paymentApiErrorMessage, setPaymentApiErrorMessage] = useState('');
  const [hasForbiddenRequestError, setHasForbiddenRequestError] = useState(false);

  const setPaymentOverlays: SetPaymentOverlayType = (isInProgress: boolean) => {
    setShowOverlaySpinner(isInProgress);
    setEnableSubmitButton(!isInProgress);
  };

  const inputErrors = useMemo((): (string | undefined)[] => {
    if (!paymentInfoErrors) return [];

    return Object.values(paymentInfoErrors.errors).filter(Boolean);
  }, [paymentInfoErrors]);

  const isEmailTouchedAndValid = isTouchedAndValid(emailInputState);

  const isFirstNameTouchedAndValid = isTouchedAndValid(nameInputState.firstName);
  const isLastNameTouchedAndValid = isTouchedAndValid(nameInputState.lastName);
  const isBusinessNameTouchedAndValid = isTouchedAndValid(businessNameInputState);
  const isPhoneNumberTouchedAndValid = isTouchedAndValid(phoneNumberInputState);
  const isStreetAddress1TouchedAndValid = isTouchedAndValid(addressInputState.streetAddress1);
  const isStreetAddress2Valid = isValid(addressInputState.streetAddress2);
  const isCityTouchedAndValid = isTouchedAndValid(addressInputState.city);
  const isStateTouchedAndValid = isTouchedAndValid(addressInputState.state);
  const isZipTouchedAndValid = isTouchedAndValid(addressInputState.zip);

  const isMaxVariableAmtError = isPayWidget && total > MAX_VARIABLE_AMOUNT;

  useEffect(() => {
    setEnableSubmitButton((prevState: boolean) => {
      // Check pending amount cause its possible someone is paying for the entire amount without CC / Ach
      return pendingAmount === 0 || (inputErrors.length === 0 && (paymentInfoErrors?.isAllTouched || false) && prevState);
    });
  }, [inputErrors, paymentInfoErrors, pendingAmount]);

  // technically not possible at this point
  if (!cloverElementsInstance || !cloverInstance) return null;

  const onFormSubmit = async (e: FormEvent) => {
    e?.preventDefault();
    let recaptchaToken = undefined;
    const sources: Source[] = [];
    const isAch = paymentMethod === 'ach';
    const sourceType = !isAch ? 'CARD' : 'ACH';

    if (isRecaptchaEnabled) {
      recaptchaToken = await doRecaptcha(recaptchaRef, setShowPaymentApiError);
      if (!recaptchaToken) {
        return;
      }
    }

    // Don't show the overlay as ach add's its own modal overlay.
    if (!isAch) setPaymentOverlays(true);

    const amountTobeDeductedFromCard = (isPayWidget && !giftCards.length) ? payWidgetAmount : pendingAmount;
    // gift cards need to account for tip/convFee since finite balance
    const giftCardTotal = payableTotalAmount + tipAmount + convenienceFee;

    if (amountTobeDeductedFromCard !== 0) {
      const tokenizedCard = await tokenize(cloverInstance, isAch, false, {
        amount: total,
      });
      sources.push({
        source: tokenizedCard.token,
        sourceType,
        amount: amountTobeDeductedFromCard,
      });
    }

    // Add any gift cards to the cTokens
    const giftCardSources = giftCards.map((g, idx) => ({
      source: g.token,
      sourceType: 'GIFT_CARD' as const,
      amount: (idx === giftCards.length - 1 && !sources.length) ? calculateFinalGiftCardAmount(giftCards, giftCardTotal) : g.balance,
    }));
    sources.push(...giftCardSources);

    // Show the overlay if not already showing after ach completed.
    !showOverlaySpinner && setPaymentOverlays(true);

    const response = await submitPayment(sources, undefined, recaptchaToken);
    if (response) {
      handleSuccessRedirect(
        navigate,
        response,
        isInvoiceMode,
        checkoutSessionId,
        merchantSettings?.SUCCESS_URL,
      );
    }
  };
  const onWalletSubmit = async (
    wallet: WalletResponse,
    notifyWalletOfPayment: NotifyWalletOfPaymentType,
    sourceType: SourceType,
    recaptchaToken: string,
  ) => {

    const sources = [{
      source: wallet.token,
      sourceType,
      amount: isPayWidget ? payWidgetAmount : total
    }];
    const payResponse = await submitPayment(sources, wallet, recaptchaToken);
    // Close the apple pay sheet with correct pay status.
    notifyWalletOfPayment(payResponse);
    if (payResponse?.id) {
      handleSuccessRedirect(
        navigate,
        payResponse,
        isInvoiceMode,
        checkoutSessionId,
        merchantSettings?.SUCCESS_URL,
      );
    }
  };

  const submitPayment = async (sources: Source[], walletToken?: WalletResponse, recaptchaToken?: string) => {
    try {
      // Clear any errors if present
      setShowPaymentApiError(false);
      setPaymentApiErrorMessage('');
      setHasForbiddenRequestError(false);

      // Todo: Remove this when customer object is properly formatted.
      // Right now we have 2 versions of customer objects one with email and another with emailAddresses
      const customerRequest = {
        ...getNames(customerInfo, walletToken),
        phoneNumber: getPhoneNumber(customerInfo),
        email: getCustomerEmail(customerInfo, walletToken),
        address: getAddressInfo(customerInfo, locale, walletToken),
        customerMetaData: customerInfo?.metadata,
      };

      const response = await pay(
        isInvoiceMode,
        isPayWidget,
        sources,
        checkoutSessionId,
        customerRequest,
        isPayWidget ? payWidgetAmount : total,
        orderNote,
        recaptchaToken,
      );

      return response;
    } catch (e) {
      const error = e as AxiosError;
      // eslint-disable-next-line no-console
      console.log('Error in submitPayment');

      const { FAIL_URL } = merchantSettings || {};

      if (!isInvoiceMode) {
        // Forbidden error will be thrown when checkout session is being paid
        // for more than max pay attempts (5 for now)
        if (isForbiddenRequestError(e)) {
          if (FAIL_URL) {
            // eslint-disable-next-line
            window.location.href = FAIL_URL;
          }

          setHasForbiddenRequestError(true);
        }
      }

      if (
        error?.response?.status === 400 &&
        error?.response?.data?.message === 'Invalid email is provided.'
      ) {
        setEmailInputState({
          hasError: true,
          isTouched: true,
          invalidEmail: getCustomerEmail(customerInfo, walletToken),
        });
      } else {
        setShowPaymentApiError(true);
        const message = getApiErrorMessage(error, t);
        setPaymentApiErrorMessage(message);
      }
    } finally {
      setPaymentOverlays(false);
    }
  };

  const displayFormErrors =
    (showPaymentErrors && !!inputErrors.length) ||
    !isEmailTouchedAndValid ||
    !isFirstNameTouchedAndValid ||
    !isLastNameTouchedAndValid ||
    !isBusinessNameTouchedAndValid ||
    !isPhoneNumberTouchedAndValid ||
    !isStreetAddress1TouchedAndValid ||
    !isStreetAddress2Valid ||
    !isCityTouchedAndValid ||
    !isStateTouchedAndValid ||
    !isZipTouchedAndValid ||
    showNoteError;

  const hasMaxPayAttemptsError = isCheckoutMode && (hasForbiddenRequestError || hasMaxPayAttemptsReached);

  const errorFields = {
    firstName: nameInputState.firstName.hasError,
    lastName: nameInputState.lastName.hasError,
    businessName: businessNameInputState.hasError,
    phoneNumber: phoneNumberInputState.hasError,
    email: emailInputState.hasError,
    streetAddress1: addressInputState.streetAddress1.hasError,
    streetAddress2: addressInputState.streetAddress2.hasError,
    city: addressInputState.city.hasError,
    state: addressInputState.state.hasError,
    zip: addressInputState.zip.hasError,
    showNoteError,
  };

  let errorMsg = '';
  Object.entries(errorFields).some(([key, value]) => {
    if (value && key !== 'email') {
      return (errorMsg = t(`customerInformation.errors.${key}`));
    } else if (value && key === 'email') {
      return (errorMsg = t('emailError'));
    } else if (showNoteError) {
      return (errorMsg = t('note.error'));
    } else {
      return (errorMsg = inputErrors[0]!);
    }
  });

  const enablePayBtn = () => {
    if (!isCustomerInfoFeatureEnabled) return false;

    const {
      ECOMM_CHECKOUT_CUSTOMER_INFO_BUSINESS_NAME_REQUIRED: isBusinessNameRequired,
      ECOMM_CHECKOUT_CUSTOMER_INFO_PHONE_NUMBER_REQUIRED: isPhoneNumberRequired,
      ECOMM_CHECKOUT_CUSTOMER_INFO_BILLING_ADDRESS_REQUIRED: isAddressRequired,
    } = customerInfoSettings;

    const errorInfo = {
      nameFields: !isFirstNameTouchedAndValid || !isLastNameTouchedAndValid,
      businessNameField: isBusinessNameRequired === '1' && !isBusinessNameTouchedAndValid,
      phoneNumberField: isPhoneNumberRequired === '1' && !isPhoneNumberTouchedAndValid,
      addressFields:
        isAddressRequired === '1' &&
        (!isStreetAddress1TouchedAndValid ||
          !isStreetAddress2Valid ||
          !isCityTouchedAndValid ||
          !isStateTouchedAndValid ||
          !isZipTouchedAndValid),
    };

    return Object.values(errorInfo).includes(true);
  };

  const validEnablePayBtn = !isInvoiceMode && enablePayBtn();

  return (
    <>
      {showOverlaySpinner && <OverlaySpinner />}

      <form onSubmit={onFormSubmit} noValidate={true} className={styles.form}>
        {isRecaptchaEnabled && <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={recaptchaSiteKey} />}


        <div className={clsx({ [styles.hideWalletBtns]: subtotal <= 0 })}>
          <WalletButtons
            cloverInstance={cloverInstance}
            cloverElementsInstance={cloverElementsInstance}
            total={total}
            isInvoiceMode={isInvoiceMode}
            checkoutSessionId={checkoutSessionId}
            successUrl={merchantSettings?.SUCCESS_URL}
            onWalletSubmit={onWalletSubmit}
            setPaymentOverlays={setPaymentOverlays}
            customerInfo={customerInfo}
            isRecaptchaEnabled={isRecaptchaEnabled}
            recaptchaRef={recaptchaRef}
          />
        </div>

        {!isInvoiceMode && (
          <>
            <h3 className={styles.sectionInfo}>{t('customerInformation.title')}</h3>
            <CustomerInfo
              customer={customer}
              emailInputState={emailInputState}
              setEmailInputState={setEmailInputState}
              nameInputState={nameInputState}
              setNameInputState={setNameInputState}
              businessNameInputState={businessNameInputState}
              setBusinessNameInputState={setBusinessNameInputState}
              phoneNumberInputState={phoneNumberInputState}
              setPhoneNumberInputState={setPhoneNumberInputState}
              addressInputState={addressInputState}
              setAddressInputState={setAddressInputState}
              setCustomerInfo={setCustomerInfo}
              customerFields={customerInfoSettings}
              isCustomerInfoFeatureEnabled={isCustomerInfoFeatureEnabled}
            />
          </>
        )}

        {merchantPaymentConfig && (
          <PaymentMethods total={total} selection={paymentMethod} setSelection={setPaymentMethod}  setEnableSubmitButton={setEnableSubmitButton}/>
        )}

        <PaymentSourceForm
          paymentMethod={paymentMethod}
          setShowPaymentErrors={setShowPaymentErrors}
          setPaymentInfoErrors={setPaymentInfoErrors}
          setEnableSubmitButton={setEnableSubmitButton}
          setOrderNote={setOrderNote}
          orderNote={orderNote}
          setShowNoteError={setShowNoteError}
          displayFormErrors={displayFormErrors}
          showPaymentApiError={showPaymentApiError}
          paymentApiErrorMessage={paymentApiErrorMessage}
          hasMaxPayAttemptsError={hasMaxPayAttemptsError}
          errorMsg={errorMsg}
        />
        <Button
          testId={CLOVER_PAYMENT_SECTION_IDS.payButton}
          className={styles.payButton}
          disabled={
            !enableSubmitButton ||
            !total ||
            !isEmailTouchedAndValid ||
            validEnablePayBtn ||
            hasMaxPayAttemptsError ||
            !isMinAmtSatisfied ||
            isMaxVariableAmtError ||
            showNoteError
          }
        >
          {t('pay', { value: fromCentsWithLocale(total, currency) })}
        </Button>

        <p className={styles.privacyPolicy}>
          <Trans i18nKey="payPrivacyPolicy">
            By Paying, you agree to Clover's
            <a href="/consumer/terms">Terms of Service</a> and
            <a href="/privacy-policy">Privacy Policy</a>
          </Trans>
        </p>
      </form>
    </>
  );
});

export { CloverPaymentSection };
